import React from 'react';
import { X, Info } from 'lucide-react';

// Mirrors the row tint + pill colors used on the Chargebacks list, with a plain
// explanation and the typical next step for each ChargebackStatus.
const STATUSES = [
  { key: 'IMPORTED', label: 'Imported', pill: 'bg-gray-100 text-gray-700', bar: 'bg-gray-300',
    what: 'A new claim ingested from an EDI 844 file, not yet checked.',
    next: 'Run validation (or Auto-Adjudicate) to move it forward.' },
  { key: 'VALIDATED', label: 'Validated', pill: 'bg-teal-100 text-teal-700', bar: 'bg-teal-400',
    what: 'Passed every validation check — contract, price/tolerance, customer, product, quantity, and duplicate checks.',
    next: 'Ready to approve or post.' },
  { key: 'IN_ERROR', label: 'In Error', pill: 'bg-orange-100 text-orange-700', bar: 'bg-orange-400',
    what: 'One or more checks failed, or the claim could not be adjudicated (e.g. no matching contract price, price over tolerance, unknown customer).',
    next: 'Open the claim to see the error codes and fix or reject it.' },
  { key: 'APPROVED', label: 'Approved', pill: 'bg-green-100 text-green-700', bar: 'bg-green-400',
    what: 'Adjudicated and accepted.',
    next: 'A credit memo can be issued to the wholesaler; then post it.' },
  { key: 'REJECTED', label: 'Rejected', pill: 'bg-red-100 text-red-700', bar: 'bg-red-400',
    what: 'Adjudicated and declined with a reason code (goes back to the wholesaler on the EDI 849 dispute).',
    next: 'No payment as submitted; the wholesaler may correct and resubmit.' },
  { key: 'POSTED', label: 'Posted', pill: 'bg-blue-100 text-blue-700', bar: 'bg-blue-400',
    what: 'Settled and posted to the general ledger / reconciled. A terminal state.',
    next: 'No further action needed.' },
  { key: 'SUPERSEDED', label: 'Superseded', pill: 'bg-purple-100 text-purple-700', bar: 'bg-purple-400',
    what: 'Replaced by a later EDI 844 resubmission (purpose code 04 Change, 05 Replace, or 6R Resubmission). The newer revision is the active claim.',
    next: 'Work the newer revision instead; this record is kept for audit.' },
  { key: 'CANCELLED', label: 'Cancelled', pill: 'bg-slate-200 text-slate-600', bar: 'bg-slate-300',
    what: 'Voided by an EDI 844 cancellation (purpose code 01), or cancelled during processing.',
    next: 'No action — the claim is withdrawn.' }
];

const StatusHelpModal = ({ open, onClose }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white px-5 py-4">
          <div className="flex items-center gap-2">
            <Info className="h-5 w-5 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900">Chargeback Status Guide</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-5 py-4">
          <p className="mb-4 text-sm text-gray-600">Every claim moves through these statuses. Rows on the list are tinted to match.</p>
          <ul className="space-y-3">
            {STATUSES.map((s) => (
              <li key={s.key} className={`flex gap-3 rounded-md border border-gray-100 p-3`}>
                <div className={`w-1 flex-shrink-0 rounded ${s.bar}`} />
                <div className="min-w-0">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${s.pill}`}>{s.label}</span>
                  <p className="mt-1 text-sm text-gray-800">{s.what}</p>
                  <p className="mt-0.5 text-xs text-gray-500"><span className="font-medium">Next:</span> {s.next}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="sticky bottom-0 flex justify-end border-t border-gray-200 bg-white px-5 py-3">
          <button onClick={onClose} className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700">Got it</button>
        </div>
      </div>
    </div>
  );
};

export default StatusHelpModal;
